import {
	ToothPeriodontal,
	ToothPeriodontalItem,
} from '../../../../graphql/graphql';
import useTranslation from '../../../../Hooks/useTranslation';

type PeriodontalItems = Omit<
	ToothPeriodontal,
	'mobility' | 'furcation' | '__typename'
>;
type PeriodontalItemKey = keyof PeriodontalItems;
type PeriodontalItemIssues = Omit<
	ToothPeriodontalItem,
	'depth' | 'margin' | '__typename'
>;
type PeriodontalItemMeasurements = Pick<
	ToothPeriodontalItem,
	'depth' | 'margin'
>;

const PERIODONTAL_ITEM_KEY_MAP: Record<PeriodontalItemKey, string> = {
	mesioPalatal:
		'patientTreatment.meddiDENT.dentalCross.periodontal.mesioPalatal',
	mesioBuccal: 'patientTreatment.meddiDENT.dentalCross.periodontal.mesioBuccal',
	mesioLingual:
		'patientTreatment.meddiDENT.dentalCross.periodontal.mesioLingual',
	distoPalatal:
		'patientTreatment.meddiDENT.dentalCross.periodontal.distoPalatal',
	distoBuccal: 'patientTreatment.meddiDENT.dentalCross.periodontal.distoBuccal',
	distoLingual:
		'patientTreatment.meddiDENT.dentalCross.periodontal.distoLingual',
	palatal: 'patientTreatment.meddiDENT.dentalCross.periodontal.palatal',
	buccal: 'patientTreatment.meddiDENT.dentalCross.periodontal.buccal',
	lingual: 'patientTreatment.meddiDENT.dentalCross.periodontal.lingual',
};

const PERIODONTAL_ITEM_ISSUES_MAP: Record<keyof PeriodontalItemIssues, string> =
	{
		bleeding: 'patientTreatment.meddiDENT.dentalCross.periodontal.bleeding',
		plaque: 'patientTreatment.meddiDENT.dentalCross.periodontal.plaque',
		pus: 'patientTreatment.meddiDENT.dentalCross.periodontal.pus',
		tartar: 'patientTreatment.meddiDENT.dentalCross.periodontal.tartar',
	};

const PERIODONTAL_ITEM_MEASUREMENTS_MAP: Record<
	keyof PeriodontalItemMeasurements,
	string
> = {
	margin: 'patientTreatment.meddiDENT.dentalCross.periodontal.gingivalMargin',
	depth: 'patientTreatment.meddiDENT.dentalCross.periodontal.probingDepth',
};

function parsePeriodontalIssues(issues: PeriodontalItemIssues) {
	const { t } = useTranslation();

	return Object.keys(issues)
		.filter(key => issues[key])
		.map(issue => t(PERIODONTAL_ITEM_ISSUES_MAP[issue]))
		.join(', ');
}

function parsePeriodontalMeasurements(
	measurements: PeriodontalItemMeasurements,
) {
	const { t } = useTranslation();

	return Object.entries(measurements)
		.filter(([, value]) => typeof value !== 'undefined')
		.map(
			([key, value]) => `${t(PERIODONTAL_ITEM_MEASUREMENTS_MAP[key])} ${value}`,
		)
		.join(', ');
}

export default function parsePeriodontalItemValues(
	key: PeriodontalItemKey,
	value: ToothPeriodontalItem,
) {
	const { t } = useTranslation();
	const { margin, depth, ...issues } = value;

	return [
		t(PERIODONTAL_ITEM_KEY_MAP[key]),
		parsePeriodontalIssues(issues),
		parsePeriodontalMeasurements({ margin, depth }),
	]
		.filter(Boolean)
		.join(' - ');
}
